'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Minus, Plus, RefreshCw, Search, X } from 'lucide-react'

import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { PickerStudent } from '../student-picker-list'
import type { SeatAssignmentValue } from './util'
import { useSharedTRPC } from '../../context'
import { SchoolClassSelect } from '../school-class-select'
import { StudentPickerList } from '../student-picker-list'
import { getSeatLockReason, resolveDefaultSeatAssignment } from './util'

export * from './util'

export interface AssignSeatDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: 'web' | 'school'
  organizationId: string
  value: SeatAssignmentValue | null
  onConfirm: (value: SeatAssignmentValue) => void
  /**
   * An explicit roster, replacing the dialog's own seat-lock filter.
   *
   * For RENEWAL, where eligibility is the opposite of a first purchase: the students to offer are
   * the ones already sitting in the pool being renewed, and a seat ending exactly when the new one
   * begins does not overlap it. `getSeatLockReason` only knows "does this child hold a seat today",
   * so it would hide every continuing student. The caller owns that rule instead and hands the ids
   * in; everything else about the dialog stays identical.
   */
  offerableChildIds?: string[]
  /**
   * What the dialog is choosing. `purchase` (the default) seats students who have none; `renewal`
   * continues students who are already seated, which needs its own title, description and empty
   * states — and an extra line saying when the chosen seats actually start.
   */
  mode?: 'purchase' | 'renewal'
  /** RENEWAL only: when the seats being chosen begin, and when they end. */
  renewalWindow?: { startDate: Date; endDate: Date }
  /**
   * RENEWAL only: whether EMPTY seats added here start at payment rather than with the renewed
   * term. True whenever the school holds a live term for them to ride (the ordinary case); false
   * for a lapsed pool, where the renewed term itself starts at payment and the distinction is moot.
   */
  renewalEmptySeatsStartAtPayment?: boolean
}

/** A roster row this dialog can reason about, once the seat-flavour fields have been normalised. */
interface Child {
  id: string
  name: string
  image: string | null
  heldSeatId: string | null
  heldSeatSourceType: string | null
  heldSeatOrganizationId: string | null
  heldSeatIsCommercial: boolean
  premiumId: string | null
  familyPremiumId: string | null
  schoolGrade?: string | null
  schoolClasses?: { id: string; name: string; image: string | null }[]
}

/**
 * `includeSeatFlavour` is a runtime boolean the router spreads its three flavour fields on
 * conditionally, so TypeScript types them optional even though this dialog always passes
 * `includeSeatFlavour: true` and they are always present at runtime. Narrow them back to the
 * shape `getSeatLockReason` requires.
 */
function toChild(row: {
  id: string
  name: string
  image: string | null
  heldSeatId: string | null
  heldSeatSourceType?: string | null
  heldSeatOrganizationId?: string | null
  heldSeatIsCommercial?: boolean
  premiumId: string | null
  familyPremiumId: string | null
  schoolGrade?: string | null
  schoolClasses?: { id: string; name: string; image: string | null }[]
}): Child {
  return {
    ...row,
    heldSeatSourceType: row.heldSeatSourceType ?? null,
    heldSeatOrganizationId: row.heldSeatOrganizationId ?? null,
    heldSeatIsCommercial: row.heldSeatIsCommercial ?? false,
  }
}

/**
 * Copy for a PURCHASE — new seats being handed to students who have none. Differs only by noun
 * between the school roster and a family's own children.
 */
const COPY = {
  school: {
    title: 'Berikan akses penuh ke murid',
    description: 'Pilih murid yang ingin kamu beri akses premium atau atur akses nanti.',
    noStudentsTitle: 'Belum ada murid terdaftar',
    noStudentsSubtitle: 'Mulai daftarkan murid-muridmu',
    allLockedTitle: 'Semua murid sudah memiliki akses premium 🎉',
    allLockedSubtitle: 'Tidak ada yang perlu diberi akses saat ini.',
    pickerEmptyLabel: 'Murid tidak ditemukan',
    fetchErrorTitle: 'Gagal mengambil data murid',
  },
  web: {
    title: 'Berikan akses penuh ke anak',
    description: 'Pilih anak yang ingin kamu beri akses premium atau atur akses nanti.',
    noStudentsTitle: 'Belum ada anak terdaftar',
    noStudentsSubtitle: 'Mulai daftarkan anak-anakmu',
    allLockedTitle: 'Semua anak sudah memiliki akses premium 🎉',
    allLockedSubtitle: 'Tidak ada yang perlu diberi akses saat ini.',
    pickerEmptyLabel: 'Anak tidak ditemukan',
    fetchErrorTitle: 'Gagal mengambil data anak',
  },
} as const

/**
 * Copy for a RENEWAL, where the same control answers a different question: not "who gets a seat"
 * but "who keeps theirs". Everyone in the list is already Premium, and the seats being chosen do
 * not start until the current term ends — both facts the purchase copy gets wrong.
 */
const RENEWAL_COPY = {
  school: {
    title: 'Pilih murid yang dilanjutkan',
    description: 'Murid yang dipilih akan melanjutkan Premium di periode berikutnya.',
    noStudentsTitle: 'Belum ada murid yang menempati kursi ini',
    noStudentsSubtitle: 'Tidak ada yang bisa dilanjutkan dari langganan ini',
    allLockedTitle: 'Tidak ada murid yang perlu dilanjutkan 🎉',
    allLockedSubtitle: 'Semua murid di kursi ini sudah punya akses premium untuk periode berikutnya.',
    pickerEmptyLabel: 'Murid tidak ditemukan',
    fetchErrorTitle: 'Gagal mengambil data murid',
  },
  web: {
    title: 'Pilih anak yang dilanjutkan',
    description: 'Anak yang dipilih akan melanjutkan Premium di periode berikutnya.',
    noStudentsTitle: 'Belum ada anak yang menempati kursi ini',
    noStudentsSubtitle: 'Tidak ada yang bisa dilanjutkan dari langganan ini',
    allLockedTitle: 'Tidak ada anak yang perlu dilanjutkan 🎉',
    allLockedSubtitle: 'Semua anak di kursi ini sudah punya akses premium untuk periode berikutnya.',
    pickerEmptyLabel: 'Anak tidak ditemukan',
    fetchErrorTitle: 'Gagal mengambil data anak',
  },
} as const

/**
 * "Berikan akses penuh ke siswa/anak" — picks which students/children a purchase seats.
 *
 * Produces `{ childIds, emptySeats }`: the seat count checkout charges for, and the roster
 * fulfilment stamps onto the minted seats once the invoice is paid. Nothing is assigned by
 * confirming this dialog, which is why every piece of copy here is future tense.
 */
export function AssignSeatDialog({
  open,
  onOpenChange,
  type,
  organizationId,
  value,
  onConfirm,
  offerableChildIds,
  mode = 'purchase',
  renewalWindow,
  renewalEmptySeatsStartAtPayment = false,
}: AssignSeatDialogProps) {
  const trpc = useSharedTRPC()
  const copy = mode === 'renewal' ? RENEWAL_COPY[type] : COPY[type]

  const [selected, setSelected] = useState<string[]>([])
  const [emptySeats, setEmptySeats] = useState(0)
  /**
   * What the seat field shows while it is being typed in, or `null` when it simply mirrors
   * `emptySeats`.
   *
   * Without it the field cannot be cleared: emptying it parses to 0, and a controlled input
   * re-rendered as "0" puts the caret after a digit the buyer is trying to delete, so typing "12"
   * over it yields "012". The draft holds the empty string; `emptySeats` is 0 meanwhile, which is
   * what the total and the price should read for a blank field anyway.
   */
  const [emptySeatsDraft, setEmptySeatsDraft] = useState<string | null>(null)
  const [schoolClassId, setSchoolClassId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Guards the one-time default computation below from re-firing on every render once data
  // arrives; cleared on close so the NEXT open recomputes from fresh data / the latest `value`.
  const initializedRef = useRef(false)

  const {
    data: childrenData,
    isPending: isPendingChildren,
    isError: isErrorChildren,
    refetch: refetchChildren,
  } = useQuery({
    ...trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
      includeSeatFlavour: true,
    }),
    enabled: open,
  })

  const {
    data: poolsData,
    isPending: isPendingPools,
    isError: isErrorPools,
    refetch: refetchPools,
  } = useQuery({
    ...trpc.myOrganization.getSeatPools.queryOptions(),
    enabled: open,
  })

  const allChildren = useMemo<Child[]>(() => (childrenData?.data ?? []).map(toChild), [childrenData])
  const trialPool = useMemo(
    () => (poolsData ?? []).find((pool) => pool.sourceType === 'free_trial') ?? null,
    [poolsData],
  )

  const isLoading = isPendingChildren || isPendingPools
  // Checked before the empty states, same reason `ManageSeatsDialog` checks it: a failed fetch
  // leaves `isPending` false with no data, which would otherwise read as "no students yet".
  const isError = isErrorChildren || isErrorPools

  useEffect(() => {
    if (open) return
    initializedRef.current = false
    setSchoolClassId(null)
    setSearch('')
    // A half-typed seat count must not survive into the next open, where it would sit over a
    // freshly restored `emptySeats`.
    setEmptySeatsDraft(null)
  }, [open])

  useEffect(() => {
    if (!open || initializedRef.current) return

    // Reopening on an already-confirmed value restores exactly what was picked, rather than
    // recomputing a fresh default over it.
    if (value) {
      setSelected(value.childIds)
      setEmptySeats(value.emptySeats)
      initializedRef.current = true
      return
    }

    if (isLoading || isError) return

    // Opened before `NewForm` settled its own default (the roster was still loading when the buyer
    // reached for the pencil). Same ladder, same helper, so the two paths cannot disagree.
    const defaultAssignment = resolveDefaultSeatAssignment({ children: allChildren, organizationId, trialPool })

    setSelected(defaultAssignment.childIds)
    setEmptySeats(defaultAssignment.emptySeats)
    initializedRef.current = true
  }, [open, value, isLoading, isError, allChildren, trialPool, organizationId])

  const lockedIds = useMemo(
    () => allChildren.filter((child) => getSeatLockReason(child, organizationId) !== null).map((child) => child.id),
    [allChildren, organizationId],
  )

  /**
   * The roster this dialog actually offers: everyone a locked seat rules out is left OUT of the
   * list, not shown greyed with a badge.
   *
   * A student already sitting on a commercial seat, a grant, or a seat another organization pays
   * for is not a decision this purchase can make — showing them made the picker read as a roster
   * with most rows unclickable, and on a school that had adopted Premium the handful of students
   * actually buyable were buried among them. Own free-trial holders stay: fulfilment releases that
   * seat, so upgrading them IS a choice.
   *
   * `stateAllLocked` below still speaks for the case where this leaves nobody.
   */
  const offerableChildren = useMemo(() => {
    if (offerableChildIds) {
      const allowed = new Set(offerableChildIds)
      return allChildren.filter((child) => allowed.has(child.id))
    }

    const locked = new Set(lockedIds)
    return allChildren.filter((child) => !locked.has(child.id))
  }, [allChildren, lockedIds, offerableChildIds])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return offerableChildren.filter((child) => {
      const matchClass =
        schoolClassId === null
          ? true
          : schoolClassId === NO_SCHOOL_CLASS_ID
            ? (child.schoolClasses?.length ?? 0) === 0
            : (child.schoolClasses?.some((schoolClass) => schoolClass.id === schoolClassId) ?? false)
      const matchSearch = term === '' || child.name.toLowerCase().includes(term)
      return matchClass && matchSearch
    })
  }, [offerableChildren, schoolClassId, search])

  /**
   * Rows for the picker: name plus "Tingkat • Kelas".
   *
   * School only. A family picks between its own two or three children, who have neither a grade
   * recorded here nor a class to belong to, so the line would render as a stray bullet.
   */
  const pickerStudents = useMemo<PickerStudent[]>(
    () =>
      filtered.map((child) => {
        const parts =
          type === 'school'
            ? [schoolGradeLabel(child.schoolGrade), child.schoolClasses?.map((item) => item.name).join(', ')].filter(
                (part): part is string => Boolean(part),
              )
            : []

        return {
          id: child.id,
          name: child.name,
          image: child.image,
          subtitle: parts.length > 0 ? parts.join(' • ') : null,
        }
      }),
    [filtered, type],
  )

  const stateNoStudents = !isLoading && !isError && allChildren.length === 0
  const stateAllLocked = !isLoading && !isError && allChildren.length > 0 && offerableChildren.length === 0

  const handleConfirm = () => {
    onConfirm({ childIds: selected, emptySeats })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:text-neutral-black srd:flex srd:h-fit srd:max-h-[95dvh] srd:flex-col srd:overflow-y-auto srd:sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>
            {copy.description}
            {mode === 'renewal' && renewalWindow ? (
              <>
                {' '}
                Kursinya aktif {formatDate(renewalWindow.startDate, { dateStyle: 'long' })} sampai{' '}
                {formatDate(renewalWindow.endDate, { dateStyle: 'long' })}, dan yang tidak dipilih tetap bisa membaca
                sampai periode sekarang berakhir.
                {/* The empty seats are the exception to "aktif <handover>": they ride the current
                    term from the moment the invoice is paid, and are priced for those days. */}
                {renewalEmptySeatsStartAtPayment
                  ? ` Kursi kosong yang kamu tambahkan bisa dipakai sejak pembayaran sampai ${formatDate(renewalWindow.endDate, { dateStyle: 'long' })}, dengan biaya pro-rata untuk sisa periode sekarang.`
                  : null}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-4 srd:py-10 srd:text-center'>
            <p
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:text-error srd:flex srd:items-center srd:gap-x-2',
              )}
            >
              <X className='srd:size-4' />
              {copy.fetchErrorTitle}
            </p>
            <Button
              size='sm'
              variant='outline'
              className='srd:gap-x-2'
              onClick={() => {
                if (isErrorChildren) void refetchChildren()
                if (isErrorPools) void refetchPools()
              }}
            >
              <RefreshCw className='srd:size-3.5' />
              Coba lagi
            </Button>
          </section>
        ) : stateNoStudents ? (
          <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-1 srd:py-10 srd:text-center'>
            <p className={typographyVariants({ variant: 'title-sm' })}>{copy.noStudentsTitle}</p>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
              {copy.noStudentsSubtitle}
            </p>
          </section>
        ) : stateAllLocked ? (
          <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-1 srd:py-10 srd:text-center'>
            <p className={typographyVariants({ variant: 'title-sm' })}>{copy.allLockedTitle}</p>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
              {copy.allLockedSubtitle}
            </p>
          </section>
        ) : (
          <section className='srd:flex srd:flex-col srd:gap-y-2'>
            <div className='srd:flex srd:w-full srd:flex-wrap srd:items-center srd:gap-2'>
              {type === 'school' && (
                <SchoolClassSelect
                  value={schoolClassId}
                  onValueChange={setSchoolClassId}
                  size='sm'
                  className='srd:w-full srd:min-w-[200px] srd:flex-1'
                  showNoClass
                />
              )}

              <InputGroup className='srd:w-full srd:min-w-[200px] srd:flex-1' size='sm'>
                <InputGroupAddon align='inline-start'>
                  <Search className='srd:size-4 srd:text-blue-100' />
                </InputGroupAddon>
                <InputGroupInput
                  type='search'
                  placeholder='Cari'
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </InputGroup>
            </div>

            {isLoading ? (
              <Skeleton className='srd:h-[240px] srd:w-full' />
            ) : (
              <StudentPickerList
                students={pickerStudents}
                selected={selected}
                onSelectedChange={setSelected}
                emptyLabel={copy.pickerEmptyLabel}
              />
            )}
          </section>
        )}

        {/* Sticky, with its own background: the roster scrolls on past it, so the running total and
            the stepper stay on screen while a long list is being worked through. */}
        <section className='srd:border-outline-variant srd:bg-neutral-white srd:sticky srd:bottom-0 srd:flex srd:flex-col srd:gap-y-3 srd:border-t srd:pt-4'>
          <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-end srd:gap-x-4 srd:gap-y-2'>
            <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-neutral-black')}>
              Tambah kursi untuk diisi nanti
            </p>

            <div className='srd:flex srd:items-center srd:gap-x-2'>
              <Button
                variant='outline'
                size='icon'
                className='srd:size-8 srd:rounded-full'
                aria-label='Kurangi kursi kosong'
                disabled={emptySeats <= 0}
                onClick={() => {
                  setEmptySeatsDraft(null)
                  setEmptySeats((current) => Math.max(0, current - 1))
                }}
              >
                <Minus className='srd:size-4' />
              </Button>

              <Input
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                aria-label='Jumlah kursi kosong'
                className={cn(
                  typographyVariants({ variant: 'label-md' }),
                  'srd:text-neutral-black srd:w-14 srd:data-[size=default]:h-10 srd:text-center',
                )}
                value={emptySeatsDraft ?? String(emptySeats)}
                onChange={(event) => {
                  // Digits only, and never a sign or a decimal point: seats are whole and cannot be
                  // negative, so the stepper's own bounds are the only ones the field needs.
                  const digits = event.target.value.replace(/\D/g, '')
                  setEmptySeatsDraft(digits)
                  setEmptySeats(digits === '' ? 0 : Number(digits))
                }}
                onBlur={() => setEmptySeatsDraft(null)}
              />

              <Button
                variant='outline'
                size='icon'
                className='srd:size-8 srd:rounded-full'
                aria-label='Tambah kursi kosong'
                onClick={() => {
                  setEmptySeatsDraft(null)
                  setEmptySeats((current) => current + 1)
                }}
              >
                <Plus className='srd:size-4' />
              </Button>
            </div>
          </div>

          {/* The emphasis rides `title-md` — same 16px as the sentence's `label-lg`, one weight up —
              rather than a bare `font-bold` over it. */}
          <p className={cn(typographyVariants({ variant: 'label-lg' }), 'srd:text-neutral-black srd:text-right')}>
            <span className={typographyVariants({ variant: 'title-md' })}>Total:</span>{' '}
            <span className={typographyVariants({ variant: 'title-md' })}>{selected.length}</span> kursi terisi,{' '}
            <span className={typographyVariants({ variant: 'title-md' })}>{emptySeats}</span> kursi kosong
          </p>
        </section>

        <DialogFooter>
          <Button disabled={isLoading || isError} onClick={handleConfirm}>
            → Pilih paket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
